import * as THREE from 'three';
import { createTranslator } from './translator';
import { convertToPolyMesh, convertToCNSPolyMesh, checkGeometryCache, cacheGeometry } from './bingeom';
import { primitiveToMaterial, getCubeMapTexture, assignTexture, getTextureImage } from './materials';
import { polyMeshToBufferGeometry } from './polyMesh';
import { createLight, updateLight } from './light';
import { setMatrixOn } from './transform';
import { initCamera, createCamera, initCameraRadiusConstraint, resizeCamera, updateLookatTarget } from './camera';
import { initManipulators } from './manipulators';
import setMatrixOnNode from './utils/setMatrixOnNode';

import { getScreenPositionNormalAlpha } from './annotation';
import { getSceneId, reportError } from '../modules/sceneIO';
import { initNode, replace, virtualCameraId, virtualTargetId } from '../modules/sceneGraph';
import { getWorldTransform } from '../modules/scene';
import { getRenderingMode, isDeviceTracking, RENDER_MODES, setPlayerElement, setThreeScene, getCamera, setCamera } from '../modules/player';
import { getActiveCommandsWithWidgets, getTools, getCommands } from '../modules/commands';
import { getSelectedNodes, isHighlighting } from '../modules/selection';
import { isUuid } from '../modules/utils/selectors';

import OutlinePass from './utils/outlinePass'; // FIXME should be OutlinePass.js

import calculateBoundingBox from './utils/calculateBoundingBox';
import enableDeviceTracking from './utils/deviceTracking';
import FXAAShader from './utils/FXAAShader';

import ParallaxBarrierEffect from './utils/parallaxBarrierEffect'; // extend three with StereoEffect
import VRControls from './utils/vrControls';
import { ThreeExport } from '../polyMesh';

const BACKGROUND_STYLE_DEFAULT = 0;
const BACKGROUND_STYLE_COLOR = 1;
const BACKGROUND_STYLE_IMAGE = 2;
const BACKGROUND_STYLE_CUBEMAP = 3;
const BACKGROUND_STYLE_ENVIRONMENT = 4;

function initTranslator(store, context, translator) {
  let sceneGraph = store.get('sceneGraph');

  const scene = new THREE.Scene();
  //const camera = new THREE.PerspectiveCamera(75, 4 / 3, 0.1, 1000);
  const camera = new THREE.CombinedCamera( 1, 1, 75, 0.1, 1000 );

  const rendererAttrs = {
    antialias: false,
    precision: 'highp',
    alpha: true,
    preserveDrawingBuffer: true,
  };
  if (context) {
    rendererAttrs.canvas = { style: {}, addEventListener: () => {} };
    rendererAttrs.context = context;
  }

  let isIOS = false;
  let isAndroid = false;
  let isMSPhone = false;
  let isMobile = false;

  const renderer = new THREE.WebGLRenderer(rendererAttrs);
  renderer.setClearColor(0xffffff);
  renderer.autoResizeTextures = false;
  renderer.gammaInput = true;
  renderer.gammaOutput = true;
  renderer.toneMapping = THREE.ReinhardToneMapping;
  renderer.physicallyCorrectLights = true;
  if (__BROWSER__) {
    let pixelRatio = window.devicePixelRatio;
    renderer.setPixelRatio(pixelRatio);
    if( window.navigator ) {
      let userAgent = window.navigator.userAgent;
      isIOS = /iPad|iPhone|iPod/.test(userAgent);
      isAndroid = /Android/i.test(userAgent);
      isMSPhone = /IEMobile|Windows Phone|Lumia/i.test(userAgent)
      isMobile = isIOS || isAndroid || isMSPhone || /Blackberry|webOS/i.test(userAgent);
    }
  }
  renderer.isMobile = isMobile;

  store.dispatch(setThreeScene(scene, camera, renderer));

  let el = null;
  let playerEl = null; // This is the parent element that we control (div.claraplayer)

  const nodes = {};
  let translated = {
    // [ nodeId ]: { [PlugName]: _v }
  };
  const annotations = {};
  const rect = new THREE.Vector2(400, 300);
  let tools = [];
  let interactionHandler = null;
  let cameraId = virtualCameraId;     // The camera we want to move
  let fromCameraId = virtualCameraId; // The camera we want to use to initialize camera position
  let translatedCameraId = null;      // The camera that we initialized from
  let pivotId = null;
  let loaded = null;
  let reflectiveFloorMaterial = null;
  let reflectiveFloorMesh = null;

  let vrControls;
  let trackingHandler;

  const externals = {};

  const translatorState = {};

  function getContext() {
    return renderer.getContext();
  }

  let selectedNodes = getSelectedNodes(store);

  // use ssaa on the server
  const serverRenderingMode = !!context;

  const composer = new THREE.EffectComposer(renderer);

  const clearPass = new THREE.ClearPass();
  clearPass.enabled = true;
  composer.addPass(clearPass);

  const texturePass = new THREE.TexturePass();
  texturePass.clear = false;
  texturePass.enabled = false;
  composer.addPass(texturePass);

  const cubeTexturePass = new THREE.CubeTexturePass(scene, camera);
  cubeTexturePass.clear = false;
  cubeTexturePass.enabled = false;
  composer.addPass(cubeTexturePass);

  const ssaaRenderPass = new THREE.SSAARenderPass(scene, camera);
  ssaaRenderPass.clear = false;
  ssaaRenderPass.sampleLevel = serverRenderingMode ? 2 : 1;
  ssaaRenderPass.unbiased = true;
  ssaaRenderPass.enabled = true;
  composer.addPass(ssaaRenderPass);

  const renderPass = new THREE.RenderPass(scene, camera);//, null, 0xffffff);
  //renderPass.clear = false;
  //renderPass.enabled = false;
  composer.addPass(renderPass);

  const fullWidth = __BROWSER__ ? window.innerWidth : 400;
  const fullHeight = __BROWSER__ ? window.innerHeight : 300;

  const effectFXAA = new THREE.ShaderPass(FXAAShader);
  effectFXAA.uniforms['resolution'].value.set(1 / fullWidth, 1 / fullHeight);
  composer.addPass(effectFXAA);

  const saoPass = new THREE.SAOPass(scene, camera);
  saoPass.downSamplingRatio = serverRenderingMode ? 1 : 1; // fast enough if not full screen (I think.)
  saoPass.intensity = 3;
  saoPass.occlusionSphereWorldRadius = 2;
  saoPass.blurEnabled = true;
  //saoPass.outputOverride = "default";
  saoPass.manualCompositing = false;
  saoPass.blurKernelSize = ((saoPass.downSamplingRatio === 1) ? 6 : 6) + (serverRenderingMode ? 2 : 0);
  saoPass.enabled = true;
  if( ! isMobile ) {
    composer.addPass(saoPass);
  }

  const dofPass = new THREE.DofPass(new THREE.Vector2(fullWidth, fullHeight), scene, camera);
  composer.addPass(dofPass);

  const bloomPass = new THREE.UnrealBloomPass(new THREE.Vector2(fullWidth, fullHeight), 1.5, 1, 1);
  composer.addPass(bloomPass);

  const outlinePass = new OutlinePass(new THREE.Vector2(fullWidth, fullHeight), scene, camera);
  outlinePass.edgeStrength = 10;
  outlinePass.edgeGlow = 0;
  outlinePass.edgeThickness = 1;
  outlinePass.pulsePeriod = 0;
  outlinePass.visibleEdgeColor = new THREE.Color(0, 1, 0);
  outlinePass.hiddenEdgeColor = new THREE.Color(0, 1, 0);
  composer.addPass(outlinePass);

  const copyPass = new THREE.ShaderPass(THREE.CopyShader);
  copyPass.renderToScreen = true;
  composer.addPass(copyPass);

  const stereoEffect = new ParallaxBarrierEffect(renderer);

  function resize(width, height) {
    const renderingMode = getRenderingMode(store);
    if (el && renderingMode !== RENDER_MODES.vr) {
      const elRect = el.parentNode.getBoundingClientRect();
      width = elRect.width;
      height = elRect.height;
    } else if(renderingMode === RENDER_MODES.vr){
      width = window.innerWidth;
      height = window.innerHeight;
    }

    if (width < 10 || height < 10) return;

    rect.set(width, height);

    if(renderingMode !== RENDER_MODES.vr){
      resizeCamera(store, cameraId, rect);
      renderer.setSize(rect.width, rect.height);
    }

    if( reflectiveFloorMaterial !== null ) {
      reflectiveFloorMaterial.setSize( rect.width, rect.height );
    }

    const pixelRatio = renderer.getPixelRatio();
    composer.setSize(rect.width * pixelRatio, rect.height * pixelRatio);

    /*console.log( 'pixelDeviceRatio', pixelRatio );
    console.log( 'renderer size', rect.width, rect.height );
    console.log( 'composer size', rect.width * pixelRatio, rect.height * pixelRatio );
    console.log( 'pixel size', rect.width * window.devicePixelRatio, rect.height * window.devicePixelRatio );*/

    if (interactionHandler) interactionHandler.resize(rect);

    if (height && height !== 0) {
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }

    effectFXAA.uniforms['resolution'].value.set(1 / width, 1 / height);
  }

  /*
   * Attach the renderer's canvas element to the DOM?.
   */
  function attach(element) {
    el = element;
    el.insertBefore(renderer.domElement, el.firstChild);
    renderer.domElement.tabIndex = 1;

    playerEl = el.parentElement;
    store.dispatch(setPlayerElement(playerEl, renderer.domElement));
    resize();
  }

  function deviceTrackingTransition(enabled){
    if(enabled){
      trackingHandler = enableDeviceTracking(store, externals);
    } else {
      if(__BROWSER__) window.removeEventListener('deviceorientation', trackingHandler);
      trackingHandler = null;
    }
  }

  function handleStateChanges() {
    // TODO: only do this if the player has changed. Requires moving the cameraWorldMatrix
    // out of the player state.
    //

    function stateChange(key, handler) {
      const newState = store.getIn(['player', key]);
      if (translatorState[key] !== newState) {
        handler(newState, translatorState[key]);
        translatorState[key] = newState;
      }
    }

    if (__BROWSER__) stateChange('deviceTracking', deviceTrackingTransition);
  }

  function highlightSelection(rootNodeId) {
    const node = rootNodeId && nodes[rootNodeId];
    outlinePass.selectedObjects = node ? [node] : [];
  }

  /*
   * After all nodes have been translated.
   */
  let renderDisplaySceneTimeout = null;
  let lastRenderStartTime = -1;
  let lastRenderDeltaTime = -1;

  function updateRenderTime() {
    const currentRenderStartTime = Date.now();
    if (lastRenderStartTime > 0) {
      lastRenderDeltaTime = currentRenderStartTime - lastRenderStartTime;
    } else {
      lastRenderDeltaTime = -1;
    }

    lastRenderStartTime = currentRenderStartTime;
  }

  function postTranslate() {
    const renderingMode = getRenderingMode(store);

    if (renderDisplaySceneTimeout) {
      clearTimeout(renderDisplaySceneTimeout);
    }

    updateRenderTime();

    const playerCameraId = getCamera(store);

    // If we are switching the threejs camera, it should already be initialized
    // (from VR controls, or previous camera, so just directly translate it)
    if (cameraId !== playerCameraId) {
      cameraId = playerCameraId;
      fromCameraId = cameraId;
      translatedCameraId = cameraId;
      translate(cameraId, sceneGraph.evaluatedNodes[cameraId], sceneGraph.nodes[cameraId], true);
    }

    if (fromCameraId !== translatedCameraId) {
      // Translate the new camera
      translate(cameraId, sceneGraph.evaluatedNodes[cameraId], sceneGraph.nodes[cameraId], true);
    }

    handleStateChanges();

    if (interactionHandler) {
      updateTools();
      updateWidgets();
    }

    selectedNodes = getSelectedNodes(store);
    if (isHighlighting(store) && selectedNodes.length) {
      highlightSelection(selectedNodes[0]);
    } else {
      highlightSelection(null);
    }

    if (reflectiveFloorMesh && reflectiveFloorMesh.data.reflectiveFloor) {
      reflectiveFloorMaterial.distanceFade = reflectiveFloorMesh.data.reflectiveFloorFade;
      reflectiveFloorMaterial.roughness = reflectiveFloorMesh.data.reflectiveFloorRoughness;
      reflectiveFloorMaterial.metalness = reflectiveFloorMesh.data.reflectiveFloorMetallic;
      reflectiveFloorMaterial.opacity = reflectiveFloorMesh.data.reflectiveFloorOpacity;
      reflectiveFloorMaterial.fresnelStrength = reflectiveFloorMesh.data.reflectiveFloorFresnel;
      reflectiveFloorMaterial.reflectivity = 0.5;

      renderer.setClearColor(renderPass.clearColor, renderPass.clearAlpha);
      reflectiveFloorMaterial.render(renderer, scene, camera, rect.width, rect.height);

      //reflectiveFloorMesh.material = new THREE.MeshStandardMaterial({ emissive: 0xff00ff, emissiveMap: reflectiveFloorMaterial.material.uniforms.tReflection.value });
    }
    //renderer.setClearColor(renderPass.clearColor, renderPass.clearAlpha);

    cubeTexturePass.camera = camera;
    if (renderingMode === RENDER_MODES.normal) {
      //renderer.autoClear = true;
      renderer.setClearColor(0xffffff);
      renderPass.enabled = false;
      effectFXAA.enabled = false;
      ssaaRenderPass.enabled = true;
      //renderer.setClearAlpha(0.9);
      composer.render();
    } else if (renderingMode === RENDER_MODES.vr) {
      renderPass.enabled = true;
      effectFXAA.enabled = true;
      ssaaRenderPass.enabled = false;
      if (!externals.vrEffect) {
        throw new Error('Expect vr mode to initialize vrEffect');
      }

      externals.vrEffect.render(scene, camera, null, true);
    } else if (renderingMode === RENDER_MODES.lenticular) {
      stereoEffect.render(scene, camera);
    }

    if (renderingMode === RENDER_MODES.normal) {
      if (!serverRenderingMode || lastRenderDeltaTime > 0) {
        renderDisplaySceneTimeout = setTimeout(function () {

          updateRenderTime();

          ssaaRenderPass.sampleLevel = 2;
          composer.render();
          ssaaRenderPass.sampleLevel = 1;

          lastRenderStartTime = -1;
          /*

          renderDisplaySceneTimeout = setTimeout(function () {

            updateRenderTime();

            ssaaRenderPass.sampleLevel = 3;
            composer.render();
            ssaaRenderPass.sampleLevel = 1;
          }, Math.max(lastRenderDeltaTime, 100) * 4);*/
        }, Math.max(lastRenderDeltaTime, 100) * 4);
      }
    }
  }

  function updateTools() {
    const commands = getCommands(store);
    if (tools !== getTools(store)) {
      tools = getTools(store);
      interactionHandler.updateTools(tools.map((n) => commands[n]).toArray());
    }
  }

  function initWidgets(command, name, rootName) {
    nodes[rootName] = new THREE.Object3D();
    nodes[rootName].name = name;
    nodes.widgets.add(nodes[rootName]);
    if (command.options.displayGizmo !== false) command.widget.draw.call(command, nodes[rootName]);
  }

  function removeWidgets(command, threeObject, name, rootName) {
    const widget = command.widget;
    if (widget && widget.remove) {
      widget.remove.call(command, threeObject);
    }

    nodes.widgets.remove(threeObject);
    delete nodes[rootName];
  }

  function updateWidgets() {
    const commands = getCommands(store);
    const activeCommandsWithWidgets = getActiveCommandsWithWidgets(store);

    nodes.widgets.children.forEach((child) => {
      if (!activeCommandsWithWidgets[child.name]) {
        removeWidgets(commands[child.name], child, child.name, `widgetsRoot-${child.name}`);
      }
    });

    Object.values(activeCommandsWithWidgets).forEach((command) => {
      const { name, widget } = command;
      const rootName = `widgetsRoot-${name}`;
      if (!nodes[rootName]) initWidgets(command, name, rootName);
      if (widget && widget.position) widget.position.call(command, nodes[rootName]);
    });
  }

  /*
   * Scene is loaded.
   */
  function onLoad() {
    loaded = true;
    const playerData = sceneGraph.evaluatedNodes[getSceneId(store)].Player;

    if (!playerData.orbitTarget) {
      const bbCenter = calculateBoundingBox(scene).getCenter();
      if (isNaN(bbCenter.x) || isNaN(bbCenter.y) || isNaN(bbCenter.z)) bbCenter.set(0, 0, 0);
      setMatrixOnNode(store, virtualTargetId, new THREE.Matrix4().makeTranslation(bbCenter.x, bbCenter.y, bbCenter.z));
    }

    if (!context) {
      interactionHandler = initManipulators(store, this, renderer.domElement);
      interactionHandler.resize(rect);
    }

    nodes.widgets = new THREE.Object3D();
    nodes.widgets.name = 'Widgets';
    scene.add(nodes.widgets);
  }

  function getAnnotations() {
    return annotations;
  }

  function addToParent(id, node, obj, skipReplace) {
    // console.log('Add toParent: ', node && node.get('parent'), obj);
    const parentId = node && node.parent;
    const parent = (parentId && nodes[parentId]) || scene;
    obj.name = node.name; // eslint-disable-line no-param-reassign
    obj.nodeId = id;

    if (nodes[id]) {
      if (skipReplace) return;

      const children = [].concat(nodes[id].children);
      for (let i = 0; i < children.length; i++) {
        nodes[id].remove(children[i]);
        obj.add(children[i]);
      }

      parent.remove(nodes[id]);
      if (nodes[id].geometry) nodes[id].geometry.dispose();
    }

    nodes[id] = obj;
    parent.add(nodes[id]);
  }

  const plugs = {
    PolyMesh: (id, data, node, properties) => {
      const visible = properties.visible;
      const { mesh, previousMesh } = data;

      //const collapseMaps = true;
      const indexingMode = renderer.extensions.get('OES_element_index_uint') ? ThreeExport.CompactIndices : ThreeExport.NoIndices;
      const interleaved = false;
      const oldMesh = nodes[id];
      const prevTriangulation = oldMesh && oldMesh.userData.triangulation;
      const prevIndexing = oldMesh && oldMesh.userData.renderIndexing;
      const prevValueMap = oldMesh && oldMesh.userData.valueMap;
      const bufferInfo = mesh && ThreeExport.exportBufferGeometry(mesh, indexingMode, interleaved, null, previousMesh, prevTriangulation, prevIndexing, prevValueMap);

      let geometry, triangulation, triangulationChanged, renderIndexing, valueMap;
      if (bufferInfo) {
        geometry = bufferInfo.geometry;
        triangulation = bufferInfo.triangulation;
        triangulationChanged = bufferInfo.triangulationChanged;
        renderIndexing  = bufferInfo.renderIndexing;
        valueMap = bufferInfo.valueMap;
      }

      let obj;

      if (triangulationChanged || !oldMesh) {
        obj = geometry && new THREE.Mesh(geometry, primitiveToMaterial(store));
      } else {
        obj = oldMesh;
        if (geometry) {
          obj.geometry.dispose();
          obj.geometry = geometry;
        }
        //obj.material.dispose();
        //obj.material = primitiveToMaterial(store);
      }

      if (obj) {
        obj.visible = visible;
        obj.userData.triangulation = triangulation;
        obj.userData.renderIndexing = renderIndexing;
        obj.userData.valueMap = valueMap;
      }

      addToParent(id, node, obj || new THREE.Object3D());
    },

    Light: (id, data, node) => {
      let obj = nodes[id];
      if (!obj) {
        obj = createLight(data);
        if (obj) addToParent(id, node, obj);
      } else {
        updateLight(data, obj);
      }
    },

    Bone: (id, data, node) => {
      if (!nodes[id]) addToParent(id, node, new THREE.Object3D());
    },

    Null: (id, data, node) => {
      if (!nodes[id]) addToParent(id, node, new THREE.Object3D());
    },

    PrefetchMaterialNull: (id, data, node, properties) => {
      if (loaded && properties.prefetch && !nodes[id]) {
        //console.log('Add a node for prefetch of material: ', node.name);
        addToParent(id, node, new THREE.Object3D());
      }
    },

    Transform: (id, data, node) => {
      if (!data || !data.transform) return;
      if (nodes[id]) setMatrixOn(nodes[id], data.transform);
    },

    Material: (id, data) => {
      if (!nodes[id]) return;
      nodes[id].material = primitiveToMaterial(store, renderer, data, nodes[id].material);
    },

    Image: (id, data, node, properties) => {
      if (loaded && properties.prefetch) getTextureImage(store, data);
    },

    Environment: (id, data) => {
      const backgroundStyle = data.backgroundStyle;
      clearPass.enabled = false;
      cubeTexturePass.enabled = false;

      switch (backgroundStyle) {

        case BACKGROUND_STYLE_COLOR:
          clearPass.enabled = true;
          clearPass.clearColor = data.backgroundColor;
          clearPass.clearAlpha = data.backgroundOpacity;
          break;
        case BACKGROUND_STYLE_IMAGE:
          texturePass.enabled = true;
          texturePass.map = assignTexture(store, renderer, data.background);
          break;
        case BACKGROUND_STYLE_CUBEMAP:
        case BACKGROUND_STYLE_ENVIRONMENT: {

          clearPass.enabled = true;
          clearPass.clearColor = 0x000000;
          clearPass.clearAlpha = 0.0;

          cubeTexturePass.enabled = true;

          let cubeMapNode;
          let cubeMapBlurring;

          if (backgroundStyle === BACKGROUND_STYLE_ENVIRONMENT) {
            cubeMapNode = data.environmentMap;
            if (cubeMapNode) {
              cubeMapNode = cubeMapNode.specularCubeMap;
            }

            cubeMapBlurring = data.environmentMapBlurring;
          } else {
            cubeMapNode = data.cubeMap;
            cubeMapBlurring = data.cubeMapBlurring;
          }

          if (cubeMapNode) {
            cubeTexturePass.envMap = getCubeMapTexture(store, renderer, cubeMapNode, true);
            cubeTexturePass.envMapIntensity = 1.0;
            cubeTexturePass.opacity = data.backgroundOpacity;
            cubeTexturePass.roughness = data.cubeMapBlurring;
          } else {
            cubeTexturePass.enabled = false;
          }
        }

          break;

        default:
          clearPass.enabled = true;
          clearPass.clearColor = new THREE.Color(1, 1, 1);
          clearPass.clearAlpha = 0;
          break;
      }
    },

    Player: (id, data, node) => {
      if (data.camera && data.camera !== fromCameraId && sceneGraph.nodes[data.camera]) {
        const fromNode = sceneGraph.nodes[data.camera];
        const cameraNode = sceneGraph.nodes[cameraId];
        const copiedNode = initNode(fromNode);
        const target = copiedNode.plugs.Transform.find((op) => op.target);
        if (target) copiedNode.plugs.Camera[0].targeted = true;
        store.dispatch(replace(cameraId, Object.assign(cameraNode, { plugs: copiedNode.plugs })));
        fromCameraId = data.camera;
      }

      if (data.orbitTarget && data.orbitTarget !== pivotId) {
        pivotId = data.orbitTarget;
        // FIXME: remove scale here?
        setMatrixOnNode(store, virtualTargetId, getWorldTransform(store, pivotId));
      }

      const styleToToneMapping = {
        Linear: THREE.LinearToneMapping,
        Reinhard: THREE.ReinhardToneMapping,
        Cineon: THREE.CineonToneMapping,
        Filmic: THREE.Uncharted2ToneMapping,
      };

      renderer.toneMapping = styleToToneMapping[data.toneMapStyle];
      renderer.toneMappingExposure = data.toneMapExposureGain;
      renderer.toneMappingWhitePoint = data.toneMapWhitePoint;
      renderer.gammaInput = true;
      renderer.gammaOutput = true;
      renderer.autoClear = false;

      saoPass.enabled = data.saoPass;
      saoPass.intensity = data.saoIntensity;
      saoPass.occlusionSphereWorldRadius = data.saoScale;

      bloomPass.enabled = data.bloomPass;
      bloomPass.threshold = data.bloomThreshold;
      //bloomPass.smoothWidth = data.bloomBlendRange;//not exposed in three.js yet

      dofPass.enabled = data.dofPass;
      //dofPass.dofMaxBlur = data.dofMaxBlur; //not exposed in three.js yet

      if (data.reflectiveFloor && ((!data.reflectiveFloorExcludeServer) || __BROWSER__)) {

        if (!reflectiveFloorMaterial) {
          const mirrorGeo = new THREE.PlaneBufferGeometry(1, 1);
          reflectiveFloorMaterial = new THREE.GlossyMirror({ clipBias: 0.003, textureWidth: 1024, textureHeight: 1024, color: 0x777777, localMirrorNormal: new THREE.Vector3(0, 1, 0) });
          reflectiveFloorMesh = new THREE.Mesh(mirrorGeo, reflectiveFloorMaterial.material);
        }

        reflectiveFloorMaterial.specularColor = new THREE.Color(0xffffff);

        reflectiveFloorMesh.scale.x = data.reflectiveFloorWidth;
        reflectiveFloorMesh.scale.y = data.reflectiveFloorWidth;
        reflectiveFloorMesh.rotation.x = -Math.PI * 0.5;
        reflectiveFloorMesh.position.y = data.reflectiveFloorHeight;

        reflectiveFloorMesh.data = data;

        if (!reflectiveFloorMesh.parent) {
          scene.add(reflectiveFloorMesh);
        }
      } else {
        if (reflectiveFloorMesh && reflectiveFloorMesh.parent) {
          scene.remove(reflectiveFloorMesh);
        }
      }
    },

    Camera: (id, data, node) => {
      if (id === cameraId && loaded) {

        camera.fov = data.fieldOfView;
        camera.aspect = data.aspectRatio;
        camera.near = data.nearClip;
        camera.far = data.farClip;

        if (data.projection === 'Orthographic') {
          //disable passes and mirror at this point
          saoPass.enabled = false;
          ssaaRenderPass.enabled = true;
          dofPass.enabled = false;
          scene.remove(reflectiveFloorMesh);

          camera.zoom = 1 / data.orthoZoom;
          camera.hyperfocusOffset = 1;
          camera.hyperfocusScale = 0;
          camera.inPerspectiveMode = false;
          camera.inOrthographicMode = true;

          // Set flags for ThreeJS camera type recognition
          camera.isOrthographicCamera = true;
          camera.isPerspectiveCamera = false;
        }
        else {
          // Set flags for ThreeJS camera type recognition
          camera.isOrthographicCamera = false;
          camera.isPerspectiveCamera = true;

          camera.inPerspectiveMode = true;
          camera.inOrthographicMode = false;
          dofPass.focalDepth = data.focalDepthEnabled ? data.focalDepth : data.targetDistance;
        }

        camera.updateProjectionMatrix();
      }

      if (data.newRadiusConstraint) initCameraRadiusConstraint(store, id, data, data.newRadiusConstraint);
    },

    CameraTransform: (id, data, node, Properties, nodeData) => {
      if (id === cameraId && loaded) {
        if (translatedCameraId !== fromCameraId || !nodeData.Camera.upPosition) {
          initCamera(store, true, fromCameraId, id, rect);

          translatedCameraId = fromCameraId;
        }

        const cameraData = nodeData.Camera;
        const renderingMode = getRenderingMode(store);
        const matrix = data.target && cameraData.targeted && cameraData.upPosition && renderingMode !== RENDER_MODES.vr && !translatorState['deviceTracking']
          ? updateLookatTarget(store, id, data, cameraData)
          : data.transform;
        setMatrixOn(camera, matrix);
      }
    },

    Annotation: (id, data, node, properties) => {
      const calcDirection = data.directionMode === 'awayFromOrigin';
      annotations[id] = {
        id: id,
        annotation: data,
        position: getScreenPositionNormalAlpha(id, calcDirection, data.directionAxis, data.directionTarget),
        visible: properties.visible,
      };
    },
  };

  plugs.BinMesh = plugs.PolyMesh;

  const order = {
    Scene: ['Player', 'Environment'],
    BinMesh: ['BinMesh', 'Transform', 'Material'],
    PolyMesh: ['PolyMesh', 'Transform', 'Material'],
    Light: ['Light', 'Transform'],
    Camera: [['Transform', 'CameraTransform'], 'Camera'],
    Null: ['Null', 'Transform'],
    Model: ['Null', 'Transform'],
    Objects: ['Null'],
    MaterialLibrary: ['Null'],
    Material: ['PrefetchMaterialNull', 'Material'], // for prefetch of material
    Image: ['Image'], // for prefetch of image
    Renderers: [],
    Passes: [],
    Pass: [],
    Renderer: [],
    Bone: ['Bone', 'Transform'],
    Annotation: ['Annotation'],
  };

  const forceDeps = {
    // When the mesh has changed, we need to run material translator again
    PolyMesh: { Material: true, Transform: true },
    BinMesh: { Material: true, Transform: true },
  };

  /*
   * Translate a node, either create or update
   */
  function translate(id, data, node, force) {
    const type = node.type;
    const processPlugs = order[type] || [];
    if (!translated[id]) translated[id] = {};
    let translateAnyway = {};
    let plug;
    let plugFunction;

    //console.log('translate: ', type, node.name, translated[id]);
    for (const nextPlug of processPlugs) {
      [plug, plugFunction] = Array.isArray(nextPlug) ? nextPlug : [nextPlug, nextPlug];

      let plugData = data[plug];
      if (force || !plugData || plugData._v !== translated[id][plug] || translateAnyway[plug]) {
        if (forceDeps[plug]) translateAnyway = forceDeps[plug];
        //console.log(' - ', plug);
        if (plugs[plugFunction]) {
          plugs[plugFunction](id, plugData, node, data.Properties, data);
          translated[id][plug] = plugData && plugData._v;
        } else {
          console.warn('Unknown plug translator for: ', plug, node.name);
        }
      }
    }
  }

  /*
   * Delete a node
   */
  function deleteNode(id) {
    const obj = nodes[id];
    const annoObj = annotations[id];

    if (obj || annoObj) delete translated[id];

    if (obj) {
      delete nodes[id];
      obj.parent.remove(obj);
    } else if (annoObj) {
      delete annotations[id];
    }
  }

  function getNodeBoundingBox(id) {
    return calculateBoundingBox(nodes[id]);
  }

  function getThreeObject(id) {
    return nodes[id];
  }

  function getCameraMouseRay(ndc) {
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, camera);// camera.inOrthographicMode ? camera.cameraO : camera.cameraP);
    return ray;
  }

  function raycastSelect(mousePosition, rootNode='Objects') {
    let hits = [];
    let selectedObjects = [];
    const ray = getCameraMouseRay(mousePosition);
    let objectsRoot = scene;

    if(isUuid(rootNode) && nodes[rootNode]){
      objectsRoot = nodes[rootNode];
    } else {
      scene.children.forEach((child)=> {
        if (child.type === 'Object3D' && child.name === rootNode) objectsRoot = child;
      });
    }

    objectsRoot.traverse((node)=> {
      if (!node.visible) return;
      if (!node.geometry) return;

      if (node.geometry.boundingBox === null) {
        node.geometry.computeBoundingBox();
      }

      const geometry = node.geometry;

      if (geometry.boundingBox.isEmpty() === false) {
        node.matrix.compose(node.position, node.quaternion, node.scale);
        node.updateMatrixWorld(true);
        const inter = ray.intersectObject(node);
        if (inter.length > 0) {
          hits.push({ id: node.nodeId, node: node, data: inter });
        }
      }
    });
    hits.sort((a, b)=> {
      return a.data[0].distance - b.data[0].distance;
    });

    return hits;
  }

  function toScreenPosition(rootNodeId) {
    if (rootNodeId) {
      const obj = nodes[rootNodeId];
      if (obj) {
        const vector = new THREE.Vector3();

        const widthHalf = 0.5 * renderer.context.canvas.width;
        const heightHalf = 0.5 * renderer.context.canvas.height;

        obj.updateMatrixWorld();
        const bbox = calculateBoundingBox(obj);
        const height = bbox.max.y - bbox.min.y;
        obj.updateMatrixWorld();
        vector.setFromMatrixPosition(obj.matrixWorld);
        vector.y += height;
        vector.project(camera);

        vector.x = (vector.x * widthHalf) + widthHalf;
        vector.y = -(vector.y * heightHalf) + heightHalf;

        return {
          x: vector.x,
          y: vector.y,
        };
      }
    }
  };

  function handheldRaycast(origin, direction, rootNode='Objects') {
    let hits = [];
    const ray = new THREE.Raycaster(origin, direction);
    let objectsRoot = scene;
    if(isUuid(rootNode) && nodes[rootNode]){
      objectsRoot = nodes[rootNode];
    } else {
      scene.children.forEach((child)=> {
        if (child.type === 'Object3D' && child.name === rootNode) objectsRoot = child;
      });
    }

    objectsRoot.traverse((node)=> {
      if (!node.visible) return;
      if (!node.geometry) return;

      if (node.geometry.boundingBox === null) {
        node.geometry.computeBoundingBox();
      }

      const geometry = node.geometry;

      if (geometry.boundingBox.isEmpty() === false) {
        node.matrix.compose(node.position, node.quaternion, node.scale);
        node.updateMatrixWorld(true);
        const inter = ray.intersectObject(node);
        if (inter.length > 0) {
          hits.push({ id: node.nodeId, node: node, data: inter });
        }
      }
    });
    hits.sort((a, b)=> {
      return a.data[0].distance - b.data[0].distance;
    });

    return hits;

  }

  function reset() {
    loaded = null;
    cameraId = virtualCameraId;
    fromCameraId = virtualCameraId;
    translatedCameraId = null;
    pivotId = null;
    translated = {};
  }

  return {
    attach,
    resize,
    translate,
    deleteNode,
    raycastSelect,
    handheldRaycast,
    getCameraMouseRay,
    getNodeBoundingBox,
    getThreeObject,
    toScreenPosition,
    postTranslate,
    onLoad,
    getAnnotations,
    getContext,
    reset,
    scene,
    camera,
    renderer,
    externals,
  };
}

export default function threeTranslator(store, context) {
  return createTranslator(store, initTranslator, context);
}



// WEBPACK FOOTER //
// ./src/translators/three.js